import React, { useState, useEffect } from 'react';
import { FaRupeeSign, FaDownload, FaRedo, FaHandHoldingHeart } from 'react-icons/fa';
import { formatCurrency } from '../utils/calculations';
import { generateGenericPDF } from '../utils/pdfGenerator';
import SEO from '../components/SEO';

const PMVVYCalculator = () => {
  const [principal, setPrincipal] = useState(1500000);
  const [payoutMode, setPayoutMode] = useState('monthly');
  const [result, setResult] = useState(null);

  // PMVVY Interest Rates (as per scheme, may vary)
  const interestRates = {
    monthly: 7.4,
    quarterly: 7.45,
    halfYearly: 7.52,
    yearly: 7.6,
  };

  const maxInvestment = 1500000; // ₹15 lakhs per senior citizen

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    calculate();
  }, [principal, payoutMode]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const calculate = () => {
    const rate = interestRates[payoutMode] / 100;
    
    let payoutAmount, payoutsPerYear;
    
    switch (payoutMode) {
      case 'monthly':
        payoutsPerYear = 12;
        payoutAmount = (principal * rate) / 12;
        break;
      case 'quarterly':
        payoutsPerYear = 4;
        payoutAmount = (principal * rate) / 4;
        break;
      case 'halfYearly':
        payoutsPerYear = 2;
        payoutAmount = (principal * rate) / 2;
        break;
      case 'yearly':
        payoutsPerYear = 1;
        payoutAmount = principal * rate;
        break;
      default:
        payoutsPerYear = 12;
        payoutAmount = (principal * rate) / 12;
    }
    
    const annualPension = payoutAmount * payoutsPerYear;
    const tenure = 10; // PMVVY tenure is 10 years
    const totalPension = annualPension * tenure;
    const totalPayout = principal + totalPension;
    
    // Generate payout schedule for first year
    const payoutSchedule = [];
    for (let i = 1; i <= payoutsPerYear; i++) {
      payoutSchedule.push({
        period: i,
        amount: Math.round(payoutAmount),
      });
    }
    
    setResult({
      payoutAmount: Math.round(payoutAmount),
      annualPension: Math.round(annualPension),
      totalPension: Math.round(totalPension),
      totalPayout: Math.round(totalPayout),
      interestRate: interestRates[payoutMode],
      payoutsPerYear,
      payoutSchedule,
    });
  };

  const handleDownloadPDF = () => {
    if (!result) return;
    const modeLabel = {
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      halfYearly: 'Half-Yearly',
      yearly: 'Yearly',
    };
    generateGenericPDF('PM Vaya Vandana Yojana Report', {
      'Investment Amount': formatCurrency(principal),
      'Payout Mode': modeLabel[payoutMode],
      'Interest Rate': `${result.interestRate}% p.a.`,
      'Pension Amount': formatCurrency(result.payoutAmount) + ` (${modeLabel[payoutMode]})`,
      'Annual Pension': formatCurrency(result.annualPension),
      'Total Pension (10 years)': formatCurrency(result.totalPension),
      'Maturity (Principal Return)': formatCurrency(principal),
      'Total Payout': formatCurrency(result.totalPayout),
    });
  };

  const handleReset = () => {
    setPrincipal(1500000);
    setPayoutMode('monthly');
  };

  const payoutModes = [
    { key: 'monthly', label: 'Monthly', rate: interestRates.monthly },
    { key: 'quarterly', label: 'Quarterly', rate: interestRates.quarterly },
    { key: 'halfYearly', label: 'Half-Yearly', rate: interestRates.halfYearly },
    { key: 'yearly', label: 'Yearly', rate: interestRates.yearly },
  ];

  return (
    <div className="calculator-page fade-in">
      <SEO 
        title="PMVVY Calculator"
        description="Free Pradhan Mantri Vaya Vandana Yojana Calculator - Calculate pension for senior citizens."
        keywords="PMVVY calculator, Pradhan Mantri Vaya Vandana Yojana calculator, senior citizen pension scheme, PMVVY pension calculator, LIC pension scheme"
        canonicalPath="/pmvvy"
      />
      <div className="calculator-header">
        <h1><FaHandHoldingHeart style={{ color: '#8b5cf6' }} /> PM Vaya Vandana Yojana (PMVVY)</h1>
        <p>Calculate pension income for senior citizens (60+ years) - LIC managed scheme</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div className="input-group">
            <label>Investment Amount (₹)</label>
            <div className="input-with-icon">
              <FaRupeeSign className="icon" />
              <input
                type="number"
                value={principal === '' ? '' : principal}
                onChange={(e) => setPrincipal(e.target.value === '' ? '' : Math.min(Number(e.target.value), maxInvestment))}
                min="100000"
                max={maxInvestment}
              />
            </div>
            <small style={{ color: 'var(--text-secondary)' }}>Min: ₹1 Lakh, Max: ₹15 Lakhs per senior citizen</small>
            <div className="slider-container">
              <input
                type="range"
                min="100000"
                max={maxInvestment}
                step="10000"
                value={principal || 0}
                onChange={(e) => setPrincipal(Number(e.target.value))}
              />
            </div>
          </div>

          <div className="input-group">
            <label>Pension Payout Mode</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', marginTop: '8px' }}>
              {payoutModes.map((mode) => (
                <button
                  key={mode.key}
                  onClick={() => setPayoutMode(mode.key)}
                  style={{
                    padding: '16px 12px',
                    border: payoutMode === mode.key ? '2px solid #8b5cf6' : '1px solid var(--border)',
                    borderRadius: '8px',
                    background: payoutMode === mode.key ? '#f5f3ff' : 'var(--surface)',
                    cursor: 'pointer',
                    textAlign: 'center',
                  }}
                >
                  <div style={{ fontWeight: payoutMode === mode.key ? 'bold' : 'normal' }}>{mode.label}</div>
                  <div style={{ fontSize: '12px', color: '#8b5cf6', marginTop: '4px' }}>
                    {mode.rate}% p.a.
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="button-group">
            <button className="btn btn-outline" onClick={handleReset}>
              <FaRedo style={{ marginRight: '8px' }} /> Reset
            </button>
            <button className="btn btn-secondary" onClick={handleDownloadPDF}>
              <FaDownload style={{ marginRight: '8px' }} /> Download PDF
            </button>
          </div>
        </div>

        <div className="calculator-results">
          {result && (
            <>
              <div className="result-summary" style={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)' }}>
                <h3>
                  {payoutMode === 'monthly' ? 'Monthly' : 
                   payoutMode === 'quarterly' ? 'Quarterly' :
                   payoutMode === 'halfYearly' ? 'Half-Yearly' : 'Yearly'} Pension
                </h3>
                <div className="amount">{formatCurrency(result.payoutAmount)}</div>
                <small>Guaranteed for 10 years @ {result.interestRate}% p.a.</small>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                <div className="result-item" style={{ background: '#ecfdf5', textAlign: 'center', padding: '16px' }}>
                  <span className="label">Annual Pension</span>
                  <span className="value" style={{ color: '#059669', fontSize: '1.2em' }}>{formatCurrency(result.annualPension)}</span>
                </div>
                <div className="result-item" style={{ background: '#eff6ff', textAlign: 'center', padding: '16px' }}>
                  <span className="label">10-Year Total Pension</span>
                  <span className="value" style={{ color: '#2563eb', fontSize: '1.2em' }}>{formatCurrency(result.totalPension)}</span>
                </div>
              </div>

              <div className="result-details">
                <div className="result-item">
                  <span className="label">Principal Invested</span>
                  <span className="value">{formatCurrency(principal)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Interest Rate</span>
                  <span className="value">{result.interestRate}% p.a.</span>
                </div>
                <div className="result-item">
                  <span className="label">Tenure</span>
                  <span className="value">10 Years</span>
                </div>
                <div className="result-item" style={{ background: '#fef3c7' }}>
                  <span className="label">Principal Returned at Maturity</span>
                  <span className="value" style={{ fontWeight: 'bold' }}>{formatCurrency(principal)}</span>
                </div>
                <div className="result-item" style={{ background: '#dcfce7' }}>
                  <span className="label">Total Payout (Pension + Principal)</span>
                  <span className="value" style={{ fontWeight: 'bold', color: '#059669' }}>{formatCurrency(result.totalPayout)}</span>
                </div>
              </div>

              {/* Payout Comparison */}
              <div className="breakup-section">
                <h4>Payout Mode Comparison</h4>
                <table className="tax-slab-table">
                  <thead>
                    <tr>
                      <th>Mode</th>
                      <th>Rate</th>
                      <th>Per Payout</th>
                      <th>Annual</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payoutModes.map((mode) => {
                      const rate = mode.rate / 100;
                      const annual = principal * rate;
                      const periods = mode.key === 'monthly' ? 12 : mode.key === 'quarterly' ? 4 : mode.key === 'halfYearly' ? 2 : 1;
                      const perPayout = annual / periods;
                      
                      return (
                        <tr key={mode.key} style={{ background: payoutMode === mode.key ? '#f5f3ff' : 'inherit' }}>
                          <td style={{ fontWeight: payoutMode === mode.key ? 'bold' : 'normal' }}>{mode.label}</td>
                          <td>{mode.rate}%</td>
                          <td>{formatCurrency(perPayout)}</td>
                          <td style={{ color: '#10b981', fontWeight: 'bold' }}>{formatCurrency(annual)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="info-box">
        <h4>📌 PMVVY Key Features</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '12px' }}>
          <div>
            <ul>
              <li>For senior citizens aged 60+ years</li>
              <li>Investment: ₹1 Lakh to ₹15 Lakhs</li>
              <li>10-year policy term</li>
              <li>Guaranteed pension payout</li>
              <li>Principal returned on maturity</li>
            </ul>
          </div>
          <div>
            <ul>
              <li>Managed by LIC of India</li>
              <li>Monthly/Quarterly/Half-yearly/Yearly pension</li>
              <li>Loan facility up to 75% after 3 years</li>
              <li>Premature exit allowed in special cases</li>
              <li>Free look period of 15 days</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PMVVYCalculator;
